/**
 * First Run State Manager - Single Source of Truth
 * 
 * Uses ONLY:
 * - profiles.onboarding_completed (DB) - gate for conversational onboarding
 * - localStorage therappi_welcome_seen_/ - gate for typewriter welcome
 * - localStorage therappi-first-time-tour-seen - gate for 13-step tour
 * - Tour controller flags - in-progress state during transitions
 * 
 * NO new DB fields. NO competing state sources.
 * 
 * First-run stages (deterministic order):
 * 1. 'onboarding' - WelcomeStep + 8-step conversational (if onboarding_completed=false)
 * 2. 'welcome' - Typewriter TherappiWelcome (if onboarding done but welcome not seen)
 * 3. 'tour' - 13-step tour (if welcome seen but tour not completed)
 * 4. 'app' - Normal dashboard (all complete)
 */

import { isTourActiveGlobal, isOnboardingTransitioning, isTourOrTransitioning } from './useTourController';

export type FirstRunStage = 'loading' | 'onboarding' | 'welcome' | 'tour' | 'app';

// LocalStorage keys (must match existing code)
const WELCOME_SEEN_KEY = 'therappi_welcome_seen_/';
const TOUR_SEEN_KEY = 'therappi-first-time-tour-seen';

/**
 * Get current localStorage state for first-run flow
 */
export function getLocalStorageState() {
  try {
    return {
      welcomeSeen: localStorage.getItem(WELCOME_SEEN_KEY) === 'true',
      tourSeen: localStorage.getItem(TOUR_SEEN_KEY) === 'true',
    };
  } catch {
    return { welcomeSeen: false, tourSeen: false };
  }
}

/**
 * Mark welcome as seen in localStorage
 */
export function markWelcomeSeen() {
  try {
    localStorage.setItem(WELCOME_SEEN_KEY, 'true');
    console.log('[FirstRunState] Marked welcome as seen');
  } catch (e) {
    console.error('[FirstRunState] Failed to mark welcome seen:', e);
  }
}

/**
 * Mark tour as seen in localStorage
 */
export function markTourSeen() {
  try {
    localStorage.setItem(TOUR_SEEN_KEY, 'true');
    console.log('[FirstRunState] Marked tour as seen');
  } catch (e) {
    console.error('[FirstRunState] Failed to mark tour seen:', e);
  }
}

/**
 * Clear all first-run localStorage (for reset/logout)
 */
export function clearFirstRunStorage() {
  try {
    localStorage.removeItem(WELCOME_SEEN_KEY);
    localStorage.removeItem(TOUR_SEEN_KEY);
    // Also clear any other welcome keys
    Object.keys(localStorage)
      .filter(k => k.startsWith('therappi_welcome_seen'))
      .forEach(k => localStorage.removeItem(k));
    console.log('[FirstRunState] Cleared all first-run storage');
  } catch (e) {
    console.error('[FirstRunState] Failed to clear storage:', e);
  }
}

/**
 * Compute the current first-run stage based on DB + localStorage + tour state
 * 
 * IMPORTANT: This is the SINGLE SOURCE OF TRUTH for rendering decisions.
 * All components must use this to determine what to show.
 */
export function computeFirstRunStage(
  onboardingCompleted: boolean | null,
  isLoading: boolean,
  hasAuthenticatedUser: boolean = true // Default true for backward compat
): FirstRunStage {
  // CRITICAL: If no authenticated user, always return 'loading'
  // This prevents acting on stale/cached profile data before auth confirms
  if (!hasAuthenticatedUser) {
    return 'loading';
  }

  // If tour is ACTUALLY running (not just transitioning), we're in the tour phase
  // Only skip to tour if tour has actually started, not during post-onboarding transition
  if (isTourActiveGlobal()) {
    return 'tour';
  }

  // Still loading - don't make decisions yet
  if (isLoading || onboardingCompleted === null) {
    return 'loading';
  }

  // Onboarding not completed in DB - must do conversational onboarding
  if (!onboardingCompleted) {
    return 'onboarding';
  }

  // Onboarding completed - check localStorage for welcome/tour state
  const { welcomeSeen, tourSeen } = getLocalStorageState();

  if (!welcomeSeen) {
    return 'welcome';
  }

  if (!tourSeen) {
    return 'tour';
  }

  return 'app';
}

/**
 * Log the current first-run state snapshot
 * Call on auth success and route changes as requested
 */
export function logFirstRunState(
  context: string,
  data: {
    path: string;
    userId?: string;
    onboardingCompleted: boolean | null;
    isLoading: boolean;
    hasUser?: boolean;
  }
) {
  const { welcomeSeen, tourSeen } = getLocalStorageState();
  const stage = computeFirstRunStage(data.onboardingCompleted, data.isLoading, data.hasUser ?? !!data.userId);
  
  console.log(`[BOOT] ${context}`, {
    path: data.path,
    user: data.userId,
    onboarding_completed: data.onboardingCompleted,
    computed_stage: stage,
    isTourActive: isTourActiveGlobal(),
    isTransitioning: isOnboardingTransitioning(),
    localStorage: {
      tourSeen: tourSeen,
      welcomeSeenRoot: welcomeSeen
    }
  });
}

/**
 * Log guard/redirect decisions
 */
export function logGuardDecision(
  reason: string,
  from: string,
  to: string,
  blocked: boolean = false
) {
  console.log('[GUARD]', { 
    reason, 
    from, 
    to,
    blocked,
    isTourActive: isTourActiveGlobal(),
    isTransitioning: isOnboardingTransitioning()
  });
}
